/**
 * A country's flag, drawn rather than fetched or typed.
 *
 * Inline SVG for the markets we support, not a dependency and not emoji.
 *
 * **Not emoji.** DESIGN.md bans emoji as iconography outright, and flag emoji
 * are worse than the usual case: Windows has never shipped glyphs for them, so
 * a flag emoji renders on that platform as two grey letter tiles. A control
 * that shows "IN" in a box to a third of the web is not a flag.
 *
 * **Not a package.** `flag-icons` and friends carry every country on earth to
 * serve our handful, and a new dependency is a reviewable event (AGENTS.md §10).
 * A few shapes are cheaper to hold than a supply-chain surface.
 *
 * Every flag is drawn into the same 4:3 box. Real flags differ — the UK is 1:2,
 * Germany 3:5 — and a row of true ratios is a ragged row. Normalising is what
 * every icon set does, and it is the right trade for a 20px chip.
 *
 * An unknown code renders nothing. There is no placeholder flag, because a grey
 * rectangle where a country should be is a country we have invented (§1).
 */

const FLAGS: Readonly<Record<string, React.ReactNode>> = {
  IN: (
    <>
      <rect width="640" height="480" fill="#f93" />
      <rect width="640" height="160" y="160" fill="#fff" />
      <rect width="640" height="160" y="320" fill="#128807" />
      <g stroke="#008" strokeWidth="4" fill="none" strokeLinecap="round">
        <circle cx="320" cy="240" r="60" />
        <line x1="320.0" y1="230.0" x2="320.0" y2="180.0" />
        <line x1="322.6" y1="230.3" x2="335.5" y2="182.0" />
        <line x1="325.0" y1="231.3" x2="350.0" y2="188.0" />
        <line x1="327.1" y1="232.9" x2="362.4" y2="197.6" />
        <line x1="328.7" y1="235.0" x2="372.0" y2="210.0" />
        <line x1="329.7" y1="237.4" x2="378.0" y2="224.5" />
        <line x1="330.0" y1="240.0" x2="380.0" y2="240.0" />
        <line x1="329.7" y1="242.6" x2="378.0" y2="255.5" />
        <line x1="328.7" y1="245.0" x2="372.0" y2="270.0" />
        <line x1="327.1" y1="247.1" x2="362.4" y2="282.4" />
        <line x1="325.0" y1="248.7" x2="350.0" y2="292.0" />
        <line x1="322.6" y1="249.7" x2="335.5" y2="298.0" />
        <line x1="320.0" y1="250.0" x2="320.0" y2="300.0" />
        <line x1="317.4" y1="249.7" x2="304.5" y2="298.0" />
        <line x1="315.0" y1="248.7" x2="290.0" y2="292.0" />
        <line x1="312.9" y1="247.1" x2="277.6" y2="282.4" />
        <line x1="311.3" y1="245.0" x2="268.0" y2="270.0" />
        <line x1="310.3" y1="242.6" x2="262.0" y2="255.5" />
        <line x1="310.0" y1="240.0" x2="260.0" y2="240.0" />
        <line x1="310.3" y1="237.4" x2="262.0" y2="224.5" />
        <line x1="311.3" y1="235.0" x2="268.0" y2="210.0" />
        <line x1="312.9" y1="232.9" x2="277.6" y2="197.6" />
        <line x1="315.0" y1="231.3" x2="290.0" y2="188.0" />
        <line x1="317.4" y1="230.3" x2="304.5" y2="182.0" />
      </g>
      <circle cx="320" cy="240" r="10" fill="#008" />
    </>
  ),
  US: (
    <>
      <rect width="640" height="480" fill="#fff" />
      <g fill="#b22234">
        <rect width="640" height="36.9" y="0" />
        <rect width="640" height="36.9" y="73.8" />
        <rect width="640" height="36.9" y="147.7" />
        <rect width="640" height="36.9" y="221.5" />
        <rect width="640" height="36.9" y="295.4" />
        <rect width="640" height="36.9" y="369.2" />
        <rect width="640" height="36.9" y="443.1" />
      </g>
      <rect width="256" height="258.5" fill="#3c3b6e" />
      <g fill="#fff">
        <polygon points="21.3,16.3 23.5,22.9 30.4,22.9 24.8,27.0 26.9,33.5 21.3,29.5 15.7,33.5 17.9,27.0 12.3,22.9 19.2,22.9" />
        <polygon points="64.0,16.3 66.1,22.9 73.0,22.9 67.5,27.0 69.6,33.5 64.0,29.5 58.4,33.5 60.5,27.0 55.0,22.9 61.9,22.9" />
        <polygon points="106.7,16.3 108.8,22.9 115.7,22.9 110.1,27.0 112.3,33.5 106.7,29.5 101.1,33.5 103.2,27.0 97.6,22.9 104.5,22.9" />
        <polygon points="149.3,16.3 151.5,22.9 158.4,22.9 152.8,27.0 154.9,33.5 149.3,29.5 143.7,33.5 145.9,27.0 140.3,22.9 147.2,22.9" />
        <polygon points="192.0,16.3 194.1,22.9 201.0,22.9 195.5,27.0 197.6,33.5 192.0,29.5 186.4,33.5 188.5,27.0 183.0,22.9 189.9,22.9" />
        <polygon points="234.7,16.3 236.8,22.9 243.7,22.9 238.1,27.0 240.3,33.5 234.7,29.5 229.1,33.5 231.2,27.0 225.6,22.9 232.5,22.9" />
        <polygon points="42.7,42.2 44.8,48.8 51.7,48.8 46.1,52.8 48.3,59.4 42.7,55.3 37.1,59.4 39.2,52.8 33.6,48.8 40.5,48.8" />
        <polygon points="85.3,42.2 87.5,48.8 94.4,48.8 88.8,52.8 90.9,59.4 85.3,55.3 79.7,59.4 81.9,52.8 76.3,48.8 83.2,48.8" />
        <polygon points="128.0,42.2 130.1,48.8 137.0,48.8 131.5,52.8 133.6,59.4 128.0,55.3 122.4,59.4 124.5,52.8 119.0,48.8 125.9,48.8" />
        <polygon points="170.7,42.2 172.8,48.8 179.7,48.8 174.1,52.8 176.3,59.4 170.7,55.3 165.1,59.4 167.2,52.8 161.6,48.8 168.5,48.8" />
        <polygon points="213.3,42.2 215.5,48.8 222.4,48.8 216.8,52.8 218.9,59.4 213.3,55.3 207.7,59.4 209.9,52.8 204.3,48.8 211.2,48.8" />
        <polygon points="21.3,68.0 23.5,74.6 30.4,74.6 24.8,78.7 26.9,85.2 21.3,81.2 15.7,85.2 17.9,78.7 12.3,74.6 19.2,74.6" />
        <polygon points="64.0,68.0 66.1,74.6 73.0,74.6 67.5,78.7 69.6,85.2 64.0,81.2 58.4,85.2 60.5,78.7 55.0,74.6 61.9,74.6" />
        <polygon points="106.7,68.0 108.8,74.6 115.7,74.6 110.1,78.7 112.3,85.2 106.7,81.2 101.1,85.2 103.2,78.7 97.6,74.6 104.5,74.6" />
        <polygon points="149.3,68.0 151.5,74.6 158.4,74.6 152.8,78.7 154.9,85.2 149.3,81.2 143.7,85.2 145.9,78.7 140.3,74.6 147.2,74.6" />
        <polygon points="192.0,68.0 194.1,74.6 201.0,74.6 195.5,78.7 197.6,85.2 192.0,81.2 186.4,85.2 188.5,78.7 183.0,74.6 189.9,74.6" />
        <polygon points="234.7,68.0 236.8,74.6 243.7,74.6 238.1,78.7 240.3,85.2 234.7,81.2 229.1,85.2 231.2,78.7 225.6,74.6 232.5,74.6" />
        <polygon points="42.7,93.9 44.8,100.4 51.7,100.4 46.1,104.5 48.3,111.1 42.7,107.0 37.1,111.1 39.2,104.5 33.6,100.4 40.5,100.4" />
        <polygon points="85.3,93.9 87.5,100.4 94.4,100.4 88.8,104.5 90.9,111.1 85.3,107.0 79.7,111.1 81.9,104.5 76.3,100.4 83.2,100.4" />
        <polygon points="128.0,93.9 130.1,100.4 137.0,100.4 131.5,104.5 133.6,111.1 128.0,107.0 122.4,111.1 124.5,104.5 119.0,100.4 125.9,100.4" />
        <polygon points="170.7,93.9 172.8,100.4 179.7,100.4 174.1,104.5 176.3,111.1 170.7,107.0 165.1,111.1 167.2,104.5 161.6,100.4 168.5,100.4" />
        <polygon points="213.3,93.9 215.5,100.4 222.4,100.4 216.8,104.5 218.9,111.1 213.3,107.0 207.7,111.1 209.9,104.5 204.3,100.4 211.2,100.4" />
        <polygon points="21.3,119.7 23.5,126.3 30.4,126.3 24.8,130.4 26.9,136.9 21.3,132.9 15.7,136.9 17.9,130.4 12.3,126.3 19.2,126.3" />
        <polygon points="64.0,119.7 66.1,126.3 73.0,126.3 67.5,130.4 69.6,136.9 64.0,132.9 58.4,136.9 60.5,130.4 55.0,126.3 61.9,126.3" />
        <polygon points="106.7,119.7 108.8,126.3 115.7,126.3 110.1,130.4 112.3,136.9 106.7,132.9 101.1,136.9 103.2,130.4 97.6,126.3 104.5,126.3" />
        <polygon points="149.3,119.7 151.5,126.3 158.4,126.3 152.8,130.4 154.9,136.9 149.3,132.9 143.7,136.9 145.9,130.4 140.3,126.3 147.2,126.3" />
        <polygon points="192.0,119.7 194.1,126.3 201.0,126.3 195.5,130.4 197.6,136.9 192.0,132.9 186.4,136.9 188.5,130.4 183.0,126.3 189.9,126.3" />
        <polygon points="234.7,119.7 236.8,126.3 243.7,126.3 238.1,130.4 240.3,136.9 234.7,132.9 229.1,136.9 231.2,130.4 225.6,126.3 232.5,126.3" />
        <polygon points="42.7,145.6 44.8,152.1 51.7,152.1 46.1,156.2 48.3,162.8 42.7,158.7 37.1,162.8 39.2,156.2 33.6,152.1 40.5,152.1" />
        <polygon points="85.3,145.6 87.5,152.1 94.4,152.1 88.8,156.2 90.9,162.8 85.3,158.7 79.7,162.8 81.9,156.2 76.3,152.1 83.2,152.1" />
        <polygon points="128.0,145.6 130.1,152.1 137.0,152.1 131.5,156.2 133.6,162.8 128.0,158.7 122.4,162.8 124.5,156.2 119.0,152.1 125.9,152.1" />
        <polygon points="170.7,145.6 172.8,152.1 179.7,152.1 174.1,156.2 176.3,162.8 170.7,158.7 165.1,162.8 167.2,156.2 161.6,152.1 168.5,152.1" />
        <polygon points="213.3,145.6 215.5,152.1 222.4,152.1 216.8,156.2 218.9,162.8 213.3,158.7 207.7,162.8 209.9,156.2 204.3,152.1 211.2,152.1" />
        <polygon points="21.3,171.4 23.5,178.0 30.4,178.0 24.8,182.0 26.9,188.6 21.3,184.6 15.7,188.6 17.9,182.0 12.3,178.0 19.2,178.0" />
        <polygon points="64.0,171.4 66.1,178.0 73.0,178.0 67.5,182.0 69.6,188.6 64.0,184.6 58.4,188.6 60.5,182.0 55.0,178.0 61.9,178.0" />
        <polygon points="106.7,171.4 108.8,178.0 115.7,178.0 110.1,182.0 112.3,188.6 106.7,184.6 101.1,188.6 103.2,182.0 97.6,178.0 104.5,178.0" />
        <polygon points="149.3,171.4 151.5,178.0 158.4,178.0 152.8,182.0 154.9,188.6 149.3,184.6 143.7,188.6 145.9,182.0 140.3,178.0 147.2,178.0" />
        <polygon points="192.0,171.4 194.1,178.0 201.0,178.0 195.5,182.0 197.6,188.6 192.0,184.6 186.4,188.6 188.5,182.0 183.0,178.0 189.9,178.0" />
        <polygon points="234.7,171.4 236.8,178.0 243.7,178.0 238.1,182.0 240.3,188.6 234.7,184.6 229.1,188.6 231.2,182.0 225.6,178.0 232.5,178.0" />
        <polygon points="42.7,197.3 44.8,203.8 51.7,203.8 46.1,207.9 48.3,214.5 42.7,210.4 37.1,214.5 39.2,207.9 33.6,203.8 40.5,203.8" />
        <polygon points="85.3,197.3 87.5,203.8 94.4,203.8 88.8,207.9 90.9,214.5 85.3,210.4 79.7,214.5 81.9,207.9 76.3,203.8 83.2,203.8" />
        <polygon points="128.0,197.3 130.1,203.8 137.0,203.8 131.5,207.9 133.6,214.5 128.0,210.4 122.4,214.5 124.5,207.9 119.0,203.8 125.9,203.8" />
        <polygon points="170.7,197.3 172.8,203.8 179.7,203.8 174.1,207.9 176.3,214.5 170.7,210.4 165.1,214.5 167.2,207.9 161.6,203.8 168.5,203.8" />
        <polygon points="213.3,197.3 215.5,203.8 222.4,203.8 216.8,207.9 218.9,214.5 213.3,210.4 207.7,214.5 209.9,207.9 204.3,203.8 211.2,203.8" />
        <polygon points="21.3,223.1 23.5,229.7 30.4,229.7 24.8,233.7 26.9,240.3 21.3,236.2 15.7,240.3 17.9,233.7 12.3,229.7 19.2,229.7" />
        <polygon points="64.0,223.1 66.1,229.7 73.0,229.7 67.5,233.7 69.6,240.3 64.0,236.2 58.4,240.3 60.5,233.7 55.0,229.7 61.9,229.7" />
        <polygon points="106.7,223.1 108.8,229.7 115.7,229.7 110.1,233.7 112.3,240.3 106.7,236.2 101.1,240.3 103.2,233.7 97.6,229.7 104.5,229.7" />
        <polygon points="149.3,223.1 151.5,229.7 158.4,229.7 152.8,233.7 154.9,240.3 149.3,236.2 143.7,240.3 145.9,233.7 140.3,229.7 147.2,229.7" />
        <polygon points="192.0,223.1 194.1,229.7 201.0,229.7 195.5,233.7 197.6,240.3 192.0,236.2 186.4,240.3 188.5,233.7 183.0,229.7 189.9,229.7" />
        <polygon points="234.7,223.1 236.8,229.7 243.7,229.7 238.1,233.7 240.3,240.3 234.7,236.2 229.1,240.3 231.2,233.7 225.6,229.7 232.5,229.7" />
      </g>
    </>
  ),
  GB: (
    <>
      <rect width="640" height="480" fill="#012169" />
      <path d="M0 0 640 480M640 0 0 480" stroke="#fff" strokeWidth="96" />
      <path
        d="M0 0 640 480M640 0 0 480"
        stroke="#c8102e"
        strokeWidth="64"
        clipPath="url(#gb-counter)"
      />
      <path d="M320 0v480M0 240h640" stroke="#fff" strokeWidth="160" />
      <path d="M320 0v480M0 240h640" stroke="#c8102e" strokeWidth="96" />
      <clipPath id="gb-counter">
        <path d="M320 240h320v240zM320 240v240H0zM320 240H0V0zM320 240V0h320z" />
      </clipPath>
    </>
  ),
  DE: (
    <>
      <rect width="640" height="160" fill="#000" />
      <rect width="640" height="160" y="160" fill="#d00" />
      <rect width="640" height="160" y="320" fill="#ffce00" />
    </>
  ),
  AE: (
    <>
      <rect width="640" height="160" fill="#00732f" />
      <rect width="640" height="160" y="160" fill="#fff" />
      <rect width="640" height="160" y="320" fill="#000" />
      <rect width="160" height="480" fill="#f00" />
    </>
  ),
  BR: (
    <>
      <rect width="640" height="480" fill="#009c3b" />
      <polygon points="54,240 320,58 586,240 320,422" fill="#ffdf00" />
      <circle cx="320" cy="240" r="112" fill="#002776" />
      {/*
        The white band is an arc of a larger circle below the globe, cut to the
        globe's edge. At a 20px chip its lettering and stars would be noise,
        so they are left out, as every small icon set does.
      */}
      <path
        d="M196 276 Q320 176 444 248"
        stroke="#fff"
        strokeWidth="24"
        fill="none"
        clipPath="url(#br-globe)"
      />
      <clipPath id="br-globe">
        <circle cx="320" cy="240" r="112" />
      </clipPath>
    </>
  ),
  SG: (
    <>
      <rect width="640" height="480" fill="#fff" />
      <rect width="640" height="240" fill="#ef3340" />
      {/*
        The crescent is two circles of the same radius, the second filled with
        the field colour. Same radius matters: different radii give a crescent
        that thickens on one horn, which is the usual tell of a hand-drawn one.
      */}
      <circle cx="150" cy="120" r="88" fill="#fff" />
      <circle cx="196" cy="120" r="88" fill="#ef3340" />
      {/* Five stars in a ring to the right of the crescent, one point up. */}
      <g fill="#fff">
        <polygon points="300.0,43.0 305.6,60.3 323.8,60.3 309.1,71.0 314.7,88.2 300.0,77.5 285.3,88.2 290.9,71.0 276.2,60.3 294.4,60.3" />
        <polygon points="349.5,78.9 355.1,96.2 373.2,96.2 358.5,106.9 364.1,124.2 349.5,113.5 334.8,124.2 340.4,106.9 325.7,96.2 343.8,96.2" />
        <polygon points="330.6,137.1 336.2,154.3 354.3,154.3 339.6,165.0 345.3,182.3 330.6,171.6 315.9,182.3 321.5,165.0 306.8,154.3 325.0,154.3" />
        <polygon points="269.4,137.1 275.0,154.3 293.2,154.3 278.5,165.0 284.1,182.3 269.4,171.6 254.7,182.3 260.4,165.0 245.7,154.3 263.8,154.3" />
        <polygon points="250.5,78.9 256.2,96.2 274.3,96.2 259.6,106.9 265.2,124.2 250.5,113.5 235.9,124.2 241.5,106.9 226.8,96.2 244.9,96.2" />
      </g>
    </>
  ),
};

/**
 * `aria-hidden` by default, and that is deliberate.
 *
 * Every place this is used prints the country's name beside it. A flag that
 * also announced "India" would have a screen reader say it twice, which is
 * noise dressed as accessibility (AGENTS.md §15a).
 */
export function CountryFlag({
  code,
  className,
}: {
  readonly code: string;
  readonly className?: string;
}) {
  const flag = FLAGS[code.toUpperCase()];
  if (flag === undefined) return null;

  return (
    <svg
      className={className === undefined ? "country-flag" : `country-flag ${className}`}
      viewBox="0 0 640 480"
      aria-hidden="true"
      focusable="false"
    >
      {flag}
    </svg>
  );
}
